/**
 * Canonical color-composition helpers.
 *
 * Three historical `withAlpha`/`withOpacity` copies grew up in different
 * modules with DIFFERENT fallback semantics for unrecognized input — two of
 * them even shared the name `withAlpha` with opposite fallbacks. They are
 * consolidated here under explicit names so an import swap can never silently
 * change fallback behavior:
 *
 *  - `withAlphaOrGray` — unrecognized input falls back to a neutral chart gray
 *    (indicator fills: a wrong-but-visible fill beats an invisible one).
 *  - `withAlphaOrKeep` — unrecognized input is returned UNCHANGED
 *    (overlays: never mangle a color the user picked).
 *  - `scaleAlpha`      — MULTIPLIES any existing alpha instead of replacing it
 *    (volume overlay: dim an already-translucent theme color further).
 */

/** `#rrggbb` → `rgba(r,g,b,alpha)`. Returns the input unchanged if it isn't a
 *  6-digit hex (so already-rgba/named colors pass through harmlessly). */
export function hexToRgba(hex: string, alpha: number): string {
  return hex.replace(/^#([0-9a-f]{6})$/i, (_, h) => {
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    return `rgba(${r},${g},${b},${alpha})`
  })
}

/** `#rrggbb` / `rgb()` / `rgba()` → `rgba()` at the given alpha. Unrecognized
 *  formats fall back to a neutral chart gray at that alpha. */
export function withAlphaOrGray(color: string, alpha: number): string {
  const hex = color.match(/^#([0-9a-fA-F]{6})$/)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`
  }
  const rgba = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*[\d.]+\s*)?\)$/,
  )
  if (rgba) return `rgba(${rgba[1]},${rgba[2]},${rgba[3]},${alpha})`
  return `rgba(120,123,134,${alpha})`
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * Compose a hex color + opacity into an `rgba()` string.
 *
 * Non-hex input (already-rgba, named colors, gradients) is returned UNCHANGED —
 * the floating drawing toolbar can hand us either, and silently mangling a
 * color the user picked is worse than ignoring the opacity for that one case.
 */
export function withAlphaOrKeep(color: string, opacity: number): string {
  if (!HEX_RE.test(color)) return color
  const a = Math.min(1, Math.max(0, opacity))
  let hex = color.slice(1)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const n = parseInt(hex, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/** Apply an opacity factor to an rgba()/rgb()/#hex color, preserving hue and
 *  multiplying any existing alpha. Unrecognized formats pass through unchanged. */
export function scaleAlpha(color: string, factor: number): string {
  const rgba = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/,
  )
  if (rgba) {
    const alpha = (rgba[4] !== undefined ? parseFloat(rgba[4]) : 1) * factor
    return `rgba(${rgba[1]},${rgba[2]},${rgba[3]},${alpha})`
  }
  const hex = color.match(/^#([0-9a-fA-F]{6})$/)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${factor})`
  }
  return color
}

/** Clamp arbitrary color input to something <input type="color"> accepts. */
export function normalizeHex(color: string): string {
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) return color
  // <input type="color"> only accepts 6-char hex; default to a safe fallback.
  return '#888888'
}

/** `#rrggbb` → channel values. Malformed slices parse to 0 rather than NaN. */
export function parseHexRgb(hex: string): { r: number; g: number; b: number } {
  return {
    r: parseInt(hex.slice(1, 3), 16) || 0,
    g: parseInt(hex.slice(3, 5), 16) || 0,
    b: parseInt(hex.slice(5, 7), 16) || 0,
  }
}

/** Channel values → `#rrggbb` (rounded — rgba() strings can carry fractions). */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')
}
